import { defineStore } from 'pinia'
import { useNotificationsStore } from '@/plugins/lib@notifications/_store/notification.store'
import { useAuthStore } from '@/plugins/w/auth/store/auth.store'
import wAxios from '@/plugins/w/axios/models/axios'

interface State {
	last_updated: Date
	countries: any
	insuranceCompanies: any
	notPrintedRequestsCount: number | null
	draftRequestsCount: number | null
	unreadResultCount: number | null,
	cities: Array<any> | null
}

export const useAppStore = defineStore('App', {
	state: (): State => ({
		last_updated: null,
		countries: null,
		insuranceCompanies: null,
		notPrintedRequestsCount: null,
		draftRequestsCount: null,
		unreadResultCount: null,
		cities: null
	}),

	actions: {
		async initializeInsuranceCompanies() {
			try {
				const insuranceCompanies = await wAxios.get_auth_data('v1/insurance-companies?per_page=100')
				this.insuranceCompanies = insuranceCompanies
			} catch (error) {
				return Promise.reject(error)
			}
		},

		async initializeCountries() {
			try {
				const countries = await wAxios.get_auth_data('v1/countries?page=1&per_page=300')
				this.countries = countries
			} catch (error) {
				return Promise.reject(error)
			}
		},

		// async initialize() {
		// 	try {
		// 		const [countries, insuranceCompanies] = await Promise.all([wAxios.get_auth_data('v1/countries?page=1&per_page=300'), wAxios.get_auth_data('v1/insurance-companies?per_page=100')])

		// 		this.countries = countries
		// 		this.insuranceCompanies = insuranceCompanies
		// 	} catch (error) {
		// 		return Promise.reject(error)
		// 	}
		// },
		async refresh() {
			// Refresh only if last update was more than 5 minutes ago
			if (this.last_updated && Date.now() - this.last_updated.getTime() < 1000 * 60 * 5) return

			this.last_updated = new Date()

			const authStore = useAuthStore()

			if (authStore.isLoggedIn && (authStore.getUser.accepted_gtc && authStore.getUser.changed_password)) {
				const notificationsStore = useNotificationsStore()

				notificationsStore.loadUnreadNotifications()
				// notificationsStore.loadNotificationsHistory()
			}
		},

		async asyncRefreshUnreadResults(query: string = '') {
			const authStore = useAuthStore()
			if (authStore.isLoggedIn) {
				try {
					const response = await wAxios.get_auth(`v1/requests?has_result=true&readed=false${query ? `&${query}` : ''}`)
					this.unreadResultCount = response?.meta?.total
				} catch (error) {
					console.error('Error:', error)
				}
			}
		}
	},

	getters: {
		getNotPrintedRequestsCount: (state) => {
			return state.notPrintedRequestsCount
		},

		getDraftRequestsCount: (state) => {
			return state.draftRequestsCount
		},

		getUnreadResultsCount: (state) => {
			return state.unreadResultCount
		},

		getCountries: (state) => {
			return state.countries
		},

		getSortedCountries: (state) => {
			if (!state.countries) return null

			const order = ['SK', 'CZ', 'AT', 'HU', 'PL']

			const sortedCountries = state.countries.sort((a, b) => {
				const orderA = order.indexOf(a.code)
				const orderB = order.indexOf(b.code)

				if (orderA === -1 && orderB === -1) {
					// If both countries are not in the order array, sort by code
					return a.code.localeCompare(b.code)
				} else if (orderA === -1) {
					// If only country A is not in the order array, country B comes first
					return 1
				} else if (orderB === -1) {
					// If only country B is not in the order array, country A comes first
					return -1
				} else {
					// If both countries are in the order array, sort by their position in the array
					return orderA - orderB
				}
			})

			return sortedCountries
		},

		getInsuranceCompanies: (state) => {
			return state.insuranceCompanies
		}
	}
})
